import { apiRequest } from '../api';
import { state } from '../state';
import { t, onLocaleChange } from '../i18n';
import type { PlayerWithStats, PlayersListResponse, PlayerResponse } from '../types';
import { navigateTo } from '../router';
import { buildPlayerCardElement } from '../card';
import { paintCardReactions } from '../ui/player-reactions-card';

interface OverlaySettings {
  animation: 'fade' | 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down' | 'none';
  autoHide: boolean;
  autoHideDelay: number;
  viewMode: 'average' | 'personal' | 'ghost';
}

interface OverlayState {
  currentPlayerId: number | null;
  /** Разовый режим показа (шорткат «В оверлей OBS» из карточки игрока). */
  currentMode?: OverlaySettings['viewMode'] | null;
  settings: OverlaySettings;
  /** Виджет инструмента сайта на оверлее (рандомизатор карт, прогноз матча и т.п.). */
  widget?: {
    kind: 'map-roll' | 'match-predict' | 'game-card';
    phase: 'rolling' | 'result';
    data:
      | { mapName?: string | null }
      | { players?: { name: string; drawn?: boolean; isWinner?: boolean }[] }
      | null;
  } | null;
  /** Сколько источников OBS реально подключено к оверлею. */
  subscribers?: number;
  version: number;
}

let abortController: AbortController | null = null;
let cachedPlayers: PlayerWithStats[] = [];
let currentState: OverlayState | null = null;
let currentToken: string | null = null;
/** Игроки, закреплённые сверху списка (передаются из «Менеджера матча»). */
let pinnedIds: number[] = [];
/** Режим закрепления: кнопки 📌/✕ у игроков видны только при включении. */
let pinMode = false;
const PINNED_KEY = 'control.pinned';

export function mountControl(params: URLSearchParams): void {
  // Проверка авторизации
  if (!state.token || !state.user) {
    sessionStorage.setItem('redirectAfterLogin', '/control');
    navigateTo('/', true);
    return;
  }

  pinnedIds = readPinnedIds(params);
  persistPinnedIds();
  // Пришли из «Менеджера матча» (?pin=…) — сразу включаем режим закрепления
  pinMode = params.has('pin');

  const screen = document.getElementById('screen-control');
  if (screen) screen.classList.remove('hidden');

  abortController = new AbortController();
  const { signal } = abortController;

  const playersBox = document.getElementById('control-players');
  const searchInput = document.getElementById('control-search') as HTMLInputElement | null;
  const statusBox = document.getElementById('control-status');
  const currentBox = document.getElementById('control-current');
  const hideBtn = document.getElementById('btn-hide');
  const animationSelect = document.getElementById('setting-animation') as HTMLSelectElement | null;
  const autoHideCheckbox = document.getElementById('setting-autohide') as HTMLInputElement | null;
  const delayInput = document.getElementById('setting-delay') as HTMLInputElement | null;
  const viewModeSelect = document.getElementById('setting-view-mode') as HTMLSelectElement | null;
  const pinModeCheckbox = document.getElementById('control-pin-mode') as HTMLInputElement | null;
  const widgetBox = document.getElementById('control-widget');
  const widgetText = document.getElementById('control-widget-text');
  const hideWidgetBtn = document.getElementById('btn-hide-widget');
  const sourcesBox = document.getElementById('control-overlay-sources');
  const sourcesWarning = document.getElementById('control-overlay-warning');
  if (pinModeCheckbox) pinModeCheckbox.checked = pinMode;

  // Кнопка игрока (общая для обеих групп)
  function buildPlayerBtn(p: PlayerWithStats): HTMLButtonElement {
    const color = getRaceColor(p.races);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'control-player-btn';
    btn.textContent = p.name;
    btn.style.setProperty('--race-color', color);
    if (currentState?.currentPlayerId === p.id) btn.classList.add('is-active');
    btn.addEventListener('click', () => void showPlayer(p.id), { signal });
    return btn;
  }

  // Строит подраздел: заголовок + кнопки игроков
  function appendGroup(box: HTMLElement, title: string, players: PlayerWithStats[]): void {
    if (players.length === 0) return;
    const header = document.createElement('div');
    header.className = 'control-group-title';
    header.textContent = title;
    box.appendChild(header);
    for (const p of players) box.appendChild(buildListItem(p));
  }

  function setPinned(id: number, pinned: boolean): void {
    pinnedIds = pinned
      ? [...pinnedIds.filter((x) => x !== id), id]
      : pinnedIds.filter((x) => x !== id);
    persistPinnedIds();
    renderPlayers(searchInput?.value ?? '');
  }

  /** Кнопка игрока + (только в режиме закрепления) боковая кнопка «закрепить»/«открепить». */
  function buildListItem(p: PlayerWithStats): HTMLElement {
    const btn = buildPlayerBtn(p);
    const isPinned = pinnedIds.includes(p.id);
    if (isPinned) btn.classList.add('is-pinned');
    // Вне режима закрепления кнопки 📌 не мешаются — показываем только игрока
    if (!pinMode) return btn;

    const wrap = document.createElement('div');
    wrap.className = isPinned ? 'control-pinned-item' : 'control-pin-item';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = isPinned ? 'control-unpin-btn' : 'control-pin-btn';
    toggle.textContent = isPinned ? '✕' : '📌';
    toggle.title = t(isPinned ? 'control.unpin' : 'control.pin');
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      setPinned(p.id, !isPinned);
    }, { signal });

    wrap.append(btn, toggle);
    return wrap;
  }

  function renderPlayers(filter: string): void {
    if (!playersBox) return;
    playersBox.innerHTML = '';

    const q = filter.toLowerCase();
    const filtered = cachedPlayers.filter((p) =>
      p.name.toLowerCase().includes(q) || (p.aka ?? '').toLowerCase().includes(q)
    );

    if (filtered.length === 0) {
      const msg = cachedPlayers.length === 0
        ? t('control.no_players')
        : t('players.nothing_found');
      playersBox.innerHTML = `<p class="hint">${msg}</p>`;
      return;
    }

    // Закреплённые из «Менеджера матча» — всегда сверху, со своей подписью
    const pinned = filtered.filter((p) => pinnedIds.includes(p.id));
    if (pinned.length > 0) {
      const header = document.createElement('div');
      header.className = 'control-group-title';
      header.textContent = t('control.pinned_title');
      header.title = t('control.pinned_hint');
      playersBox.appendChild(header);
      for (const p of pinned) playersBox.appendChild(buildListItem(p));
    }

    // Дальше — игроки с оценками и без оценок, но с играми (закреплённых не дублируем)
    const rest = filtered.filter((p) => !pinnedIds.includes(p.id));
    const rated = rest.filter((p) => p.vote_count > 0);
    const unratedWithGames = rest.filter((p) => p.vote_count === 0 && p.games_played > 0);

    appendGroup(playersBox, t('control.rated_players'), rated);
    appendGroup(playersBox, t('control.unrated_players'), unratedWithGames);
  }

  // ---- Превью карточки (как в оверлее, в уменьшенном виде) ----
  const previewBox = document.getElementById('control-preview');
  const previewCache = new Map<string, { player: PlayerWithStats; reactions: PlayerResponse['reactions'] }>();
  let previewKey: string | null = null;

  // Превью должно совпадать с оверлеем: та же карточка и те же краткие реакции.
  function paintPreview(player: PlayerWithStats, reactions: PlayerResponse['reactions']): void {
    const card = buildPlayerCardElement(player, { compact: false, withReactions: true });
    paintCardReactions(reactions ?? null, card, player.id);
    previewBox?.replaceChildren(card);
  }

  async function renderPreview(playerId: number | null): Promise<void> {
    if (!previewBox) return;

    const viewMode = currentState?.currentMode ?? currentState?.settings.viewMode ?? 'average';
    const key = playerId === null ? null : `${playerId}:${viewMode}`;

    if (playerId === null) {
      previewKey = null;
      previewBox.innerHTML = `<p class="hint">${t('control.preview_empty')}</p>`;
      return;
    }

    // Токен оверлея ещё не загружен — обновимся после его получения
    if (!currentToken) {
      previewBox.innerHTML = `<p class="hint">${t('common.loading')}</p>`;
      return;
    }

    // Ничего не изменилось — не перерисовываем заново
    if (previewKey === key) return;
    previewKey = key;

    const cached = previewCache.get(key!);
    if (cached) {
      paintPreview(cached.player, cached.reactions);
      return;
    }

    try {
      const res = await apiRequest<PlayerResponse>(
        `/api/overlay/player/${playerId}?token=${encodeURIComponent(currentToken)}`
      );
      // Пока грузили — мог поменяться показываемый игрок/режим
      if (previewKey !== key) return;
      previewCache.set(key!, { player: res.player, reactions: res.reactions ?? null });
      paintPreview(res.player, res.reactions ?? null);
    } catch {
      previewBox.innerHTML = `<p class="hint">${t('control.preview_error')}</p>`;
    }
  }

  function renderState(s: OverlayState): void {
    currentState = s;

    if (currentBox) {
      if (s.currentPlayerId === null) {
        currentBox.textContent = t('control.current_nothing');
        currentBox.classList.remove('is-active');
      } else {
        const player = cachedPlayers.find((p) => p.id === s.currentPlayerId);
        currentBox.textContent = player
          ? `${t('control.current_prefix')}${player.name}`
          : `${t('control.current_prefix')}#${s.currentPlayerId}`;
        currentBox.classList.add('is-active');
      }
    }

    if (animationSelect) animationSelect.value = s.settings.animation;
    if (autoHideCheckbox) autoHideCheckbox.checked = s.settings.autoHide;
    if (delayInput) delayInput.value = String(s.settings.autoHideDelay);
    if (viewModeSelect) viewModeSelect.value = s.settings.viewMode ?? 'average';

    renderSources(s.subscribers);
    renderWidget(s.widget ?? null);
    renderPlayers(searchInput?.value ?? '');
    void renderPreview(s.currentPlayerId);
  }

  /**
   * Сколько источников OBS реально подключено. 0 — показывать некуда:
   * без предупреждения кажется, что «оверлей не сработал».
   */
  function renderSources(count: number | undefined): void {
    if (!sourcesBox || !sourcesWarning) return;
    // Старое SSE-сообщение без счётчика не должно сбрасывать предупреждение
    if (typeof count !== 'number') return;
    sourcesBox.textContent = `${t('control.overlay_sources')} ${count}`;
    sourcesBox.classList.toggle('is-ok', count > 0);
    sourcesBox.classList.toggle('is-error', count === 0);
    sourcesWarning.classList.toggle('hidden', count > 0);
  }

  /** Строка «Виджет: <карта/прогноз>» + кнопка скрытия (виджет шлют инструменты сайта). */
  function renderWidget(widget: OverlayState['widget']): void {
    if (!widgetBox || !widgetText) return;
    if (!widget) {
      widgetBox.classList.add('hidden');
      widgetText.textContent = '';
      return;
    }
    widgetBox.classList.remove('hidden');
    let name = '…';
    if (widget.kind === 'match-predict') {
      const players =
        widget.data && 'players' in widget.data ? widget.data.players ?? [] : [];
      const drawn = players.find((p) => p.drawn) ?? players[0];
      name = `${t('control.widget_predict')}${drawn?.name ? ` · ${drawn.name}` : ''}`;
    } else if (widget.kind === 'game-card') {
      const players =
        widget.data && 'players' in widget.data ? widget.data.players ?? [] : [];
      const winner = players.find((p) => p.isWinner) ?? players[0];
      const map = widget.data && 'mapName' in widget.data ? widget.data.mapName : null;
      name = `${t('control.widget_game')}${winner?.name ? ` · ${winner.name}` : map ? ` · ${map}` : ''}`;
    } else if (widget.data && 'mapName' in widget.data) {
      name = widget.data.mapName ?? '…';
    }
    widgetText.textContent = `${t('control.widget_prefix')}${widget.phase === 'rolling' ? '…' : name}`;
  }

  async function fetchState(): Promise<void> {
    try {
      const s = await apiRequest<OverlayState>('/api/control/state', { token: state.token });
      renderState(s);
      if (statusBox) {
        statusBox.textContent = `● ${t('control.connected')}`;
        statusBox.classList.remove('is-error');
        statusBox.classList.add('is-ok');
      }
    } catch {
      if (statusBox) {
        statusBox.textContent = `● ${t('control.disconnected')}`;
        statusBox.classList.add('is-error');
        statusBox.classList.remove('is-ok');
      }
    }
  }

  async function showPlayer(playerId: number): Promise<void> {
    try {
      const res = await apiRequest<{ state: OverlayState }>('/api/control/show', {
        method: 'POST', token: state.token, body: { playerId },
      });
      renderState(res.state);
    } catch (err) {
      alert(t('control.show_error') + (err instanceof Error ? err.message : String(err)));
    }
  }

  async function hidePlayer(): Promise<void> {
    try {
      const res = await apiRequest<{ state: OverlayState }>('/api/control/hide', {
        method: 'POST', token: state.token,
      });
      renderState(res.state);
    } catch (err) {
      alert(t('control.hide_error') + (err instanceof Error ? err.message : String(err)));
    }
  }

  /** Убирает виджет инструмента с оверлея (рандомизатор и т.п.). */
  async function hideWidget(): Promise<void> {
    try {
      const res = await apiRequest<{ state: OverlayState }>('/api/control/widget', {
        method: 'POST', token: state.token, body: { kind: null },
      });
      renderState(res.state);
    } catch (err) {
      alert(t('control.settings_error') + (err instanceof Error ? err.message : String(err)));
    }
  }

  async function updateSettings(patch: Partial<OverlaySettings>): Promise<void> {
    try {
      const res = await apiRequest<{ state: OverlayState }>('/api/control/settings', {
        method: 'POST', token: state.token, body: patch,
      });
      renderState(res.state);
    } catch (err) {
      alert(t('control.settings_error') + (err instanceof Error ? err.message : String(err)));
    }
  }

  async function setupOverlayPanel(): Promise<void> {
    const urlInput = document.getElementById('overlay-url') as HTMLInputElement | null;
    const copyBtn = document.getElementById('btn-copy-url') as HTMLButtonElement | null;
    const openBtn = document.getElementById('btn-open-overlay') as HTMLAnchorElement | null;
    const regenBtn = document.getElementById('btn-regenerate-token') as HTMLButtonElement | null;

    if (!urlInput || !copyBtn || !regenBtn) return;

    let currentUrl = '';

    const updateUrl = (token: string): void => {
      currentUrl = `${window.location.origin}/overlay?token=${token}`;
      urlInput.value = currentUrl;
      if (openBtn) openBtn.href = currentUrl;
    };

    try {
      const res = await apiRequest<{ token: string }>('/api/control/token', {
        token: state.token,
      });
      currentToken = res.token;
      updateUrl(res.token);
      connectSSE(res.token);
      // Токен получен — можно подгрузить превью текущей карточки
      void renderPreview(currentState?.currentPlayerId ?? null);
    } catch (err) {
      console.error('[control] failed to fetch overlay token:', err);
      urlInput.value = t('control.token_error');
    }

    urlInput.addEventListener('focus', () => urlInput.select(), { signal });

    copyBtn.addEventListener('click', async () => {
      if (!currentUrl) return;
      try {
        await navigator.clipboard.writeText(currentUrl);
        copyBtn.classList.add('is-copied');
        const label = copyBtn.querySelector('.control-copy-label');
        const icon = copyBtn.querySelector('.control-copy-icon');
        const origLabel = label?.textContent ?? '';
        const origIcon = icon?.textContent ?? '';
        if (label) label.textContent = t('common.copied');
        if (icon) icon.textContent = '✓';
        setTimeout(() => {
          copyBtn.classList.remove('is-copied');
          if (label) label.textContent = origLabel;
          if (icon) icon.textContent = origIcon;
        }, 1800);
      } catch {
        urlInput.focus();
        urlInput.select();
        alert(t('control.copy_error'));
      }
    }, { signal });

    regenBtn.addEventListener('click', async () => {
      if (!confirm(t('control.regenerate_confirm'))) return;
      try {
        regenBtn.disabled = true;
        const res = await apiRequest<{ token: string }>('/api/control/token/regenerate', {
          method: 'POST', token: state.token,
        });
        currentToken = res.token;
        updateUrl(res.token);
        // Старые источники отключены сервером — переподключаем свою панель
        // с новым токеном, иначе её SSE останется на старом.
        connectSSE(res.token);
        await fetchState();
      } catch (err) {
        alert(t('control.regenerate_error') + (err instanceof Error ? err.message : String(err)));
      } finally {
        regenBtn.disabled = false;
      }
    }, { signal });
  }

  let controlStream: EventSource | null = null;

  function connectSSE(token: string): void {
    if (controlStream) {
      controlStream.close();
      controlStream = null;
    }
    // client=control: страница стримера не считается источником OBS
    const url = `/api/overlay/stream?token=${encodeURIComponent(token)}&client=control`;
    const es = new EventSource(url);
    controlStream = es;

    es.addEventListener('open', () => {
      if (controlStream !== es) return;
      if (statusBox) {
        statusBox.textContent = `● ${t('control.connected')}`;
        statusBox.classList.add('is-ok');
        statusBox.classList.remove('is-error');
      }
    });

    es.addEventListener('message', (e) => {
      if (controlStream !== es) return;
      try {
        const s = JSON.parse(e.data) as OverlayState;
        renderState(s);
      } catch (err) {
        console.error('[control] parse SSE error:', err);
      }
    });

    es.addEventListener('error', () => {
      console.warn('[control] SSE error, reconnecting…');
      if (statusBox) {
        statusBox.textContent = `● ${t('control.disconnected')}`;
        statusBox.classList.add('is-error');
        statusBox.classList.remove('is-ok');
      }
    });

    signal.addEventListener('abort', () => es.close());
  }

  // Инициализация
  void (async () => {
    try {
      const res = await apiRequest<PlayersListResponse>('/api/players');
      // Показываем игроков с оценками и без оценок, но с хотя бы одной игрой;
      // закреплённых из «Менеджера матча» — всегда, даже без оценок и игр.
      cachedPlayers = res.players.filter(
        (p) => p.vote_count > 0 || p.games_played > 0 || pinnedIds.includes(p.id)
      );
      renderPlayers('');
    } catch {
      if (playersBox) playersBox.innerHTML = `<p class="hint">${t('control.no_players')}</p>`;
    }

    await fetchState();
    await setupOverlayPanel();
    // Страница уже закрыта — опрос не нужен
    if (signal.aborted) return;

    // Страховка на случай, если SSE-соединение панели «зависло»:
    // состояние (в т.ч. число источников OBS) обновляем и опросом.
    // Перерисовываем только при реальном изменении, чтобы список игроков
    // не дёргался под рукой и не сбивал прокрутку.
    const pollTimer = window.setInterval(() => {
      void (async () => {
        try {
          const s = await apiRequest<OverlayState>('/api/control/state', { token: state.token });
          if (!currentState || s.version !== currentState.version) renderState(s);
          else renderSources(s.subscribers);
        } catch {
          // Связь сторожит SSE; здесь молчим, чтобы не мигать статусом
        }
      })();
    }, 15_000);
    signal.addEventListener('abort', () => clearInterval(pollTimer));
  })();

  // Обработчики UI
  hideBtn?.addEventListener('click', () => void hidePlayer(), { signal });
  hideWidgetBtn?.addEventListener('click', () => void hideWidget(), { signal });
  searchInput?.addEventListener('input', () => renderPlayers(searchInput.value), { signal });
  pinModeCheckbox?.addEventListener('change', () => {
    pinMode = pinModeCheckbox.checked;
    renderPlayers(searchInput?.value ?? '');
  }, { signal });
  animationSelect?.addEventListener('change', () => {
    void updateSettings({ animation: animationSelect.value as any });
  }, { signal });
  autoHideCheckbox?.addEventListener('change', () => {
    void updateSettings({ autoHide: autoHideCheckbox.checked });
  }, { signal });
  delayInput?.addEventListener('change', () => {
    const d = Number(delayInput.value);
    if (Number.isInteger(d) && d >= 1 && d <= 600) void updateSettings({ autoHideDelay: d });
  }, { signal });
  viewModeSelect?.addEventListener('change', () => {
    void updateSettings({ viewMode: viewModeSelect.value as any });
  }, { signal });

  // Обновление при смене языка; подписку снимаем при unmount (abort).
  const offLocale = onLocaleChange(() => {
    if (statusBox && statusBox.classList.contains('is-ok')) {
      statusBox.textContent = `● ${t('control.connected')}`;
    }
    if (currentState) renderState(currentState);
  });
  signal.addEventListener('abort', offLocale, { once: true });
}

export function unmountControl(): void {
  abortController?.abort();
  abortController = null;
  currentToken = null;
  pinnedIds = [];
  pinMode = false;
}

/** Закреплённые игроки: из ?pin=1,2,3 или из sessionStorage (переход в той же вкладке). */
function readPinnedIds(params: URLSearchParams): number[] {
  const raw = params.get('pin');
  let ids: unknown = null;
  if (raw) {
    ids = raw.split(',');
  } else {
    try {
      const stored = sessionStorage.getItem(PINNED_KEY);
      if (stored) ids = JSON.parse(stored);
    } catch {
      /* ignore */
    }
  }
  if (!Array.isArray(ids)) return [];
  return [...new Set(ids.map(Number).filter((n) => Number.isInteger(n) && n > 0))];
}

function persistPinnedIds(): void {
  try {
    sessionStorage.setItem(PINNED_KEY, JSON.stringify(pinnedIds));
  } catch {
    /* ignore */
  }
}

function getRaceColor(races: string[]): string {
  if (races.length === 0) return 'var(--race-mixed)';
  if (races.length === 1) {
    const r = races[0];
    if (r === 'T') return 'var(--race-terran)';
    if (r === 'Z') return 'var(--race-zerg)';
    if (r === 'P') return 'var(--race-protoss)';
    if (r === 'R') return 'var(--race-random)';
  }
  return 'var(--race-mixed)';
}